import notification from "antd/es/notification";
import { getLocation, push, RouterLocation } from "connected-react-router";
import { isEqual, omit } from "lodash-es";
import { defineMessages } from "react-intl";
import {
  all,
  call,
  delay,
  put,
  race,
  select,
  take,
  takeEvery,
  takeLatest,
  takeLeading,
} from "redux-saga/effects";
import invariant from "tiny-invariant";
import * as uuid from "uuid";

import ActivityStatusOption from "@mapmycustomers/shared/enum/activity/ActivityStatusOption";
import FieldFeature from "@mapmycustomers/shared/enum/fieldModel/FieldFeature";
import SortOrder from "@mapmycustomers/shared/enum/SortOrder";
import Identified from "@mapmycustomers/shared/types/base/Identified";
import { Activity, Company, Deal, EntityType, Person } from "@mapmycustomers/shared/types/entity";
import ActivityType from "@mapmycustomers/shared/types/entity/activities/ActivityType";
import Note from "@mapmycustomers/shared/types/entity/common/Note";
import Route, {
  RoutePersonStop,
  RoutePreview,
  RoutePreviewMetrics,
} from "@mapmycustomers/shared/types/entity/Route";
import IField from "@mapmycustomers/shared/types/fieldModel/IField";
import { RawFile } from "@mapmycustomers/shared/types/File";
import Organization from "@mapmycustomers/shared/types/Organization";
import User from "@mapmycustomers/shared/types/User";
import { FilterModel } from "@mapmycustomers/shared/types/viewModel/internalModel/FilterModel";
import SortModel from "@mapmycustomers/shared/types/viewModel/internalModel/SortModel";
import ListResponse from "@mapmycustomers/shared/types/viewModel/ListResponse";
import PlatformFilterModel, {
  PinLegendsFilter,
} from "@mapmycustomers/shared/types/viewModel/platformModel/PlatformFilterModel";
import PlatformSortModel from "@mapmycustomers/shared/types/viewModel/platformModel/PlatformSortModel";
import { nameComparator } from "@mapmycustomers/shared/util/comparator";

import getSuccessNotificationNode from "@app/component/createEditEntity/util/getSuccessNotificationNode";
import { RELATIONSHIP_DATA_LOAD_LIMIT } from "@app/component/preview/util/consts";
import getFileRemovedNotificationNode from "@app/component/preview/util/getFileRemovedNotificationNode";
import getStartRange from "@app/component/preview/util/getStartRange";
import getYourDownloadWillStartShortlyNode from "@app/component/preview/util/getYourDownloadWillStartShortlyNode";
import {
  getSavedActivityTypeIds,
  resolveSavedActivityTypes,
} from "@app/component/preview/util/savedActivityTypes";
import i18nService from "@app/config/I18nService";
import localSettings from "@app/config/LocalSettings";
import Path from "@app/enum/Path";
import { recapRangeIntervalMap } from "@app/enum/preview/RecapRange";
import { applyMapViewSettings, fetchPins } from "@app/scene/map/store/mapMode/actions";
import { getActiveActivityTypes } from "@app/store/activity";
import { FieldValidationErrors } from "@app/store/api/ApiService";
import { callApi } from "@app/store/api/callApi";
import { getEntityTypesWithExternalPreview, getEntityViewSource } from "@app/store/entityView";
import { showEntityView } from "@app/store/entityView/actions";
import { EntityViewSource } from "@app/store/entityView/EntityViewPayload";
import { handleError } from "@app/store/errors/actions";
import {
  getCurrentUser,
  getOrganization,
  getOrganizationId,
  shouldIncludeSharedActivities,
} from "@app/store/iam";
import { updateMetadata } from "@app/store/iam/actions";
import {
  selectPaneSourceObjectMessageArgs,
  selectSourceObjectMessageArgs,
} from "@app/store/iam/selectSourceObjectMessageArgs";
import { notifyAboutChanges } from "@app/store/uiSync/actions";
import FileListItem from "@app/types/FileListItem";
import { NOTIFICATION_DURATION_WITH_ACTION } from "@app/util/consts";
import { allSettled, SettleResult } from "@app/util/effects";
import noteFieldModel, { NoteFieldName } from "@app/util/fieldModel/NoteFieldModel";
import personFieldModel, { PersonFieldName } from "@app/util/fieldModel/PersonFieldModel";
import { downloadFileByUrl } from "@app/util/file";
import { createEmptyFilterModel } from "@app/util/filters/utils";
import { activityLayoutModel } from "@app/util/layout/impl";
import getPinLegendsFilter from "@app/util/map/getPinLegendsFilter";
import buildPreviewPayloadFromRoute from "@app/util/route/buildPreviewPayloadFromRoute";
import isRouteComplete from "@app/util/route/isRouteComplete";
import getRoutePreviewMetrics from "@app/util/route/routePreviewMetrics";
import { SourceObjectMessageArgs } from "@app/util/ui";
import { convertToPlatformSortModel } from "@app/util/viewModel/convertSort";
import { convertToPlatformFilterModel } from "@app/util/viewModel/convertToPlatformFilterModel";

import {
  addPersonCompanies,
  addPersonDeals,
  bulkUpdateFiles,
  changeActivitiesRecapRange,
  changeActivitiesSelectedActivityTypes,
  createPersonNote,
  deletePerson,
  deletePersonFile,
  deletePersonNote,
  downloadPersonFile,
  fetchCompanies,
  fetchDeals,
  fetchMoreCompanies,
  fetchNotes,
  fetchPerson,
  fetchPersonActivities,
  fetchPersonActivitiesCompletedByType,
  fetchPersonActivitiesTotal,
  fetchPersonRoutes,
  fetchPreviewData,
  fetchThumbnail,
  postponeActivity,
  removePersonCompany,
  removePersonDeal,
  removePersonFromRoute,
  setPersonColor,
  setPersonShape,
  subscribePersonEmail,
  suppressPartialAddressWarning,
  toggleCompleteActivity,
  unsubscribePersonEmail,
  updateActivityNote,
  updatePerson,
  updatePersonFrequency,
  updatePersonNote,
  updatePersonPrimaryCompany,
  uploadPersonFiles,
} from "./actions";
import PersonRecordData from "./PersonRecordData";
import { getNotesFilter, getNotesSort, getPerson, getRecordData } from "./selectors";

const messages = defineMessages({
  addCompanySuccess: {
    id: "personRecordPane.addCompany.success",
    defaultMessage:
      "{sourceObject, select, standard {Person} partnerPeople {Partner} leadPeople {Lead} staffMemberPeople {Staff Member} leadPersonnel {Lead Personnel} other {{label}}} successfully related to {count, plural, one {# Company} other {# Companies}}",
    description:
      'Person successfully related to Companies message. The record type name comes from the select: the web\'s own word for a known type, otherwise {label}, a name an admin typed or an integration sent. Keep {label} in its base form. In es, pt and fr, no article, adjective or participle may agree with it: put gendered words on the noun "record" with the name in parentheses, or put a verb before the name. In a choice that creates a record, use a form like "Добавить: {label}".',
  },
  addDealSuccess: {
    id: "personRecordPane.addDeal.success",
    defaultMessage:
      "{sourceObject, select, standard {Person} partnerPeople {Partner} leadPeople {Lead} staffMemberPeople {Staff Member} leadPersonnel {Lead Personnel} other {{label}}} successfully related to {count, plural, one {# Deal} other {# Deals}}",
    description:
      'Person successfully related to Deal. The record type name comes from the select: the web\'s own word for a known type, otherwise {label}, a name an admin typed or an integration sent. Keep {label} in its base form. In es, pt and fr, no article, adjective or participle may agree with it: put gendered words on the noun "record" with the name in parentheses, or put a verb before the name. In a choice that creates a record, use a form like "Добавить: {label}".',
  },
  deleteSuccess: {
    id: "personRecordPane.delete.success.title",
    defaultMessage:
      "{sourceObject, select, standard {Person} partnerPeople {Partner} leadPeople {Lead} staffMemberPeople {Staff Member} leadPersonnel {Lead Personnel} other {{label}}} successfully deleted",
    description:
      'Person deleted success message. The record type name comes from the select: the web\'s own word for a known type, otherwise {label}, a name an admin typed or an integration sent. Keep {label} in its base form. In es, pt and fr, no article, adjective or participle may agree with it: put gendered words on the noun "record" with the name in parentheses, or put a verb before the name. In a choice that creates a record, use a form like "Добавить: {label}".',
  },
  deleteSuccessDescription: {
    id: "personRecordPane.delete.success.description",
    defaultMessage: "To restore this record, go to Data in Settings.",
    description: "Person deleted success description",
  },
  postponeSuccess: {
    id: "personRecordPane.activities.postpone.success",
    defaultMessage: "Activity postponed successfully",
    description: "Activity postpone success message",
  },
  removeCompanySuccess: {
    id: "personRecordPane.removeCompany.success",
    defaultMessage: "Successfully removed {count, plural, one {# Company} other {# Companies}}",
    description: "Person successfully removed Companies message",
  },
  removeDealSuccess: {
    id: "personRecordPane.removeDeal.success",
    defaultMessage: "Successfully removed {count, plural, one {# Deal} other {# Deals}}",
    description: "Person successfully removed Deals",
  },
  toggleCompleteSuccess: {
    id: "personRecordPane.activities.toggleComplete.success",
    defaultMessage: 'Activity{completed, select, true {} other { is no longer}} marked as "Done"',
    description: "Activity toggle complete success message",
  },
  updateError: {
    id: "personRecordPane.update.error",
    defaultMessage:
      "Error updating {sourceObject, select, standard {person} partnerPeople {partner} leadPeople {lead} staffMemberPeople {staff member} leadPersonnel {lead personnel} other {{label}}}. Please try again or contact support",
    description:
      'Person update error message. The record type name comes from the select: the web\'s own word for a known type, otherwise {label}, a name an admin typed or an integration sent. Keep {label} in its base form. In es, pt and fr, no article, adjective or participle may agree with it: put gendered words on the noun "record" with the name in parentheses, or put a verb before the name. In a choice that creates a record, use a form like "Добавить: {label}".',
  },
  updateSuccess: {
    id: "personRecordPane.update.success",
    defaultMessage:
      "{sourceObject, select, standard {Person} partnerPeople {Partner} leadPeople {Lead} staffMemberPeople {Staff Member} leadPersonnel {Lead Personnel} other {{label}}} updated successfully",
    description:
      'Person updated success message. The record type name comes from the select: the web\'s own word for a known type, otherwise {label}, a name an admin typed or an integration sent. Keep {label} in its base form. In es, pt and fr, no article, adjective or participle may agree with it: put gendered words on the noun "record" with the name in parentheses, or put a verb before the name. In a choice that creates a record, use a form like "Добавить: {label}".',
  },
});

const personCompaniesFilter: Readonly<PlatformFilterModel> = {
  includeAccessStatus: true,
  includeCustomFields: true,
  includeGroups: true,
  includeTerritories: true,
};

const personCompaniesOrder: PlatformSortModel = "name";

function* getPersonCompaniesFilter() {
  const pinLegends: PinLegendsFilter = yield call(getPinLegendsFilter, [EntityType.COMPANY]);

  return { ...personCompaniesFilter, pinLegends };
}

// It is possible that user doesn't have access to some companies,
// and they're not returned in companiesResponse (loadedCompanies).
// However, the Platform does include them in person.accounts (includedCompanies)
// response.
// So let's pick companies from there, which were not returned by
// companies request.
function processPersonCompanies({
  accessibleCompanies,
  allCompanies,
}: {
  accessibleCompanies: Company[];
  allCompanies: Company[];
}) {
  const accessibleCompaniesMap = new Map(
    accessibleCompanies.map((company) => [company.id, company])
  );
  return allCompanies.map((company) => {
    return (
      accessibleCompaniesMap.get(company.id) ?? {
        ...company,
        accessStatus: { changeOwner: false, delete: false, read: false, update: false },
      }
    );
  });
}

function* getFetchPersonPayload(personId: Person["id"]) {
  // The response embeds person.accounts, and those companies land in the same Relationships list as
  // the separately fetched ones, so both halves have to be resolved by the same legend.
  const pinLegendFilter: PinLegendsFilter = yield call(getPinLegendsFilter, [
    EntityType.COMPANY,
    EntityType.PERSON,
  ]);

  return {
    $filters: {
      cadence: true,
      entities: {
        [EntityType.PERSON]: {
          $and: [{ id: personId }],
          includeGroups: true,
          includeTerritories: true,
        },
      },
      includeAccessStatus: true,
      includeAssociations: true,
      includeCustomFields: true,
      includeEmailSubscriptionStatus: true,
      includeUsersWithAccess: true,
      pinLegends: pinLegendFilter,
    },
    $limit: 1,
  };
}

export function* onFetchPreviewData({
  payload: personId,
}: ReturnType<typeof fetchPreviewData.request>) {
  try {
    const companiesFilter: PlatformFilterModel = yield call(getPersonCompaniesFilter);
    const dealPinLegends: PinLegendsFilter = yield call(getPinLegendsFilter, [EntityType.DEAL]);
    const organization: Organization = yield select(getOrganization);

    const response: ListResponse<Person> = yield callApi(
      "fetchPins",
      organization.id,
      yield call(getFetchPersonPayload, personId)
    );

    const person: Person | undefined = response.data[0];

    if (!person) {
      yield put(fetchPreviewData.failure());
      return;
    }

    const filesField: IField | undefined = yield call(
      [personFieldModel, personFieldModel.getByName],
      PersonFieldName.FILES
    );

    const notesField: IField | undefined = yield call(
      [personFieldModel, personFieldModel.getByName],
      PersonFieldName.NOTES
    );

    const personCompaniesSlice = person.accounts?.slice(0, RELATIONSHIP_DATA_LOAD_LIMIT) ?? [];
    const includeSharedActivities: boolean = yield select(shouldIncludeSharedActivities);

    const notesSort: SortModel = yield select(getNotesSort);

    const [
      notes,
      files,
      dealsResponse,
      activitiesResponse,
      uncompletedActivitiesResponse,
      routesResponse,
      companiesResponse,
    ]: [
      ListResponse<Note>,
      ListResponse<RawFile>,
      ListResponse<Deal>,
      ListResponse<Activity>,
      ListResponse<Activity>,
      ListResponse<Route>,
      ListResponse<Company> | undefined,
    ] = yield all([
      notesField?.isReadable
        ? callApi("fetchNotes", EntityType.PERSON, person.id, {
            $limit: 1000,
            $order: convertToPlatformSortModel(notesSort),
          })
        : { accessible: 0, data: [] },
      filesField?.isReadable
        ? callApi("fetchEntityFiles", organization.id, EntityType.PERSON, person.id, {
            $limit: 1000,
            $order: "-createdAt",
          })
        : { data: [] },
      callApi("fetchDeals", organization.id, {
        $filters: {
          $and: [
            {
              contactId: person.id,
            },
          ],
          includeAccessStatus: true,
          includeCustomFields: true,
          includeGroups: true,
          includeTerritories: true,
          pinLegends: dealPinLegends,
        },
        $limit: RELATIONSHIP_DATA_LOAD_LIMIT,
        $order: "name",
      }),
      callApi("fetchActivities", organization.id, {
        $columns: ["id", "startAt"],
        $filters: {
          $and: [
            {
              contactId: person.id,
            },
          ],
          includeContactSharedActivities: includeSharedActivities,
        },
        $limit: 0,
      }),
      callApi("fetchActivities", organization.id, {
        $columns: ["id", "startAt"],
        $filters: {
          $and: [
            {
              completed: false,
              contactId: person.id,
            },
          ],
          includeAccessStatus: true,
          includeContactSharedActivities: includeSharedActivities,
        },
        $limit: 1000,
        $order: "startAt",
      }),
      callApi("fetchUnifiedRoutes", organization.id, {
        // `contacts` is a v2 unified-routes scope filter (BE PR #9900); not typed on
        // PlatformFilterModel because adding it surfaces a pre-existing territoryMode
        // MapFilterModel spread bug (out of Wave 4.1 scope). See TART-27167 follow-up.
        $filters: {
          contacts: { $in: [person.id] },
          includeAccessStatus: true,
        } as PlatformFilterModel,
        $limit: 1000,
        $order: "name",
      }),
      personCompaniesSlice.length > 0
        ? callApi("fetchCompanies", organization.id, {
            $filters: {
              $and: [{ id: { $in: personCompaniesSlice.map(({ id }) => id) } }],
              ...companiesFilter,
            },
            $limit: RELATIONSHIP_DATA_LOAD_LIMIT,
            $order: personCompaniesOrder,
          })
        : undefined,
    ]);

    const user: User = yield select(getCurrentUser);

    const activityTypes: ActivityType[] = yield select(getActiveActivityTypes);
    const selectedActivityTypes = resolveSavedActivityTypes(
      activityTypes,
      user?.metaData?.recapChartTypesIds
    );

    const companies = processPersonCompanies({
      accessibleCompanies: companiesResponse?.data ?? [],
      allCompanies: personCompaniesSlice,
    }).sort(nameComparator);

    yield put(
      fetchPreviewData.success({
        recordData: {
          activities: [],
          activitiesCompletedByType: [],
          activitiesTotal: activitiesResponse.total,
          companies,
          deals: dealsResponse.data,
          files: files.data,
          notes: notes.data,
          notesTotal: notes.accessible,
          person,
          routes: routesResponse.data,
          totalCompanies: person.accounts.length,
          totalDeals: dealsResponse.total,
          uncompletedActivities: uncompletedActivitiesResponse.data,
        },
        selectedActivityTypes,
      })
    );
  } catch (error) {
    yield put(fetchPreviewData.failure());
    yield put(handleError({ error }));
  }
}

export function* onFetchPersonActivities({
  payload: {
    activityTypes: selectedActivityTypes,
    filter,
    order,
    personId,
    recapRange,
    search,
    selectedAssignees,
  },
}: ReturnType<typeof fetchPersonActivities.request>) {
  try {
    const organization: Organization = yield select(getOrganization);
    const recapInterval = recapRangeIntervalMap[recapRange];
    const includeSharedActivities: boolean = yield select(shouldIncludeSharedActivities);
    const activityTypes: ActivityType[] = yield select(getActiveActivityTypes);

    const activitiesResponse: ListResponse<Activity> = yield callApi(
      "fetchActivities",
      organization.id,
      {
        $filters: {
          $and: [
            {
              completed:
                filter === ActivityStatusOption.COMPLETED
                  ? true
                  : filter === ActivityStatusOption.OVERDUE
                    ? false
                    : undefined,
              contactId: personId,
              ...(selectedActivityTypes.length === activityTypes.length
                ? {}
                : { "crmActivityType.id": { $in: selectedActivityTypes.map(({ id }) => id) } }),
              name: search ? { $in: search.trim() } : undefined,
              startAt: getStartRange(recapInterval, filter),
              ...(selectedAssignees.length ? { assigneeId: { $in: selectedAssignees } } : {}),
            },
          ],
          includeAccessStatus: true,
          includeContactSharedActivities: includeSharedActivities,
          includeCustomFields: true,
          includeEmails: true,
          includeFiles: true,
        },
        $limit: 1000,
        $order: convertToPlatformSortModel(order),
      }
    );
    yield put(fetchPersonActivities.success(activitiesResponse.data));
  } catch (error) {
    yield put(fetchPersonActivities.failure());
    yield put(handleError({ error }));
  }
}

export function* onFetchPersonActivitiesCompletedByType({
  payload: {
    activityTypes: selectedActivityTypes,
    filter,
    order,
    personId,
    recapRange,
    selectedAssignees,
  },
}: ReturnType<typeof fetchPersonActivitiesCompletedByType.request>) {
  try {
    const organization: Organization = yield select(getOrganization);
    const recapInterval = recapRangeIntervalMap[recapRange];
    const includeSharedActivities: boolean = yield select(shouldIncludeSharedActivities);
    const activityTypes: ActivityType[] = yield select(getActiveActivityTypes);

    const activitiesResponse: ListResponse<Activity> = yield callApi(
      "fetchActivities",
      organization.id,
      {
        $filters: {
          $and: [
            {
              contactId: personId,
              ...(selectedActivityTypes.length === activityTypes.length
                ? {}
                : { "crmActivityType.id": { $in: selectedActivityTypes.map(({ id }) => id) } }),
              startAt: getStartRange(recapInterval, filter),
              ...(selectedAssignees.length ? { assigneeId: { $in: selectedAssignees } } : {}),
            },
          ],
          includeAccessStatus: true,
          includeContactSharedActivities: includeSharedActivities,
          includeCustomFields: true,
        },
        $limit: 1000,
        $order: convertToPlatformSortModel(order),
      }
    );
    yield put(fetchPersonActivitiesCompletedByType.success(activitiesResponse.data));
  } catch (error) {
    yield put(fetchPersonActivitiesCompletedByType.failure());
    yield put(handleError({ error }));
  }
}

export function* onFetchPersonActivitiesTotal({
  payload,
}: ReturnType<typeof fetchPersonActivitiesTotal.request>) {
  try {
    const organization: Organization = yield select(getOrganization);
    const includeSharedActivities: boolean = yield select(shouldIncludeSharedActivities);

    const activitiesResponse: ListResponse<Activity> = yield callApi(
      "fetchActivities",
      organization.id,
      {
        $columns: ["id"],
        $filters: {
          $and: [
            {
              contactId: payload,
            },
          ],
          includeContactSharedActivities: includeSharedActivities,
        },
        $limit: 0,
      }
    );
    yield put(fetchPersonActivitiesTotal.success(activitiesResponse.total));
  } catch (error) {
    yield put(fetchPersonActivitiesTotal.failure());
    yield put(handleError({ error }));
  }
}

export function* onFetchPerson({ payload: person }: ReturnType<typeof fetchPerson.request>) {
  try {
    const organization: Organization = yield select(getOrganization);
    const response: Person = yield callApi("fetchPerson", organization.id, person.id, {
      cadence: true,
      includeAccessStatus: true,
      includeGroups: true,
      includeTerritories: true,
      includeUsersWithAccess: true,
    });
    yield put(fetchPerson.success(response));
  } catch (error) {
    yield put(fetchPerson.failure());
    yield put(handleError({ error }));
  }
}

export function* onFetchPersonRoutes({ payload }: ReturnType<typeof fetchPersonRoutes.request>) {
  try {
    const organization: Organization = yield select(getOrganization);
    const routesResponse: ListResponse<Route> = yield callApi(
      "fetchUnifiedRoutes",
      organization.id,
      {
        // `contacts` v2 scope filter — see notes on the sibling call above.
        $filters: {
          contacts: { $in: [payload] },
          includeAccessStatus: true,
        } as PlatformFilterModel,
        $limit: 1000,
        $order: "name",
      }
    );

    yield put(fetchPersonRoutes.success(routesResponse.data));
    yield put(
      notifyAboutChanges({ entityType: EntityType.PEOPLE_ROUTE, updated: routesResponse.data })
    );
  } catch (error) {
    yield put(fetchPersonRoutes.failure());
    yield put(handleError({ error }));
  }
}

export function* onFetchThumbnail({ payload }: ReturnType<typeof fetchThumbnail>) {
  try {
    const org: Organization = yield select(getOrganization);
    const person: Person = yield select(getPerson);
    const fileData: Blob = yield callApi(
      "fetchFile",
      org.id,
      payload.fileId,
      false,
      true,
      EntityType.PERSON,
      person.id,
      { responseType: "blob" }
    );
    yield call(payload.callback, fileData);
  } catch (error) {
    yield put(handleError({ error }));
  }
}

function* onAddPersonCompany({
  payload: { id, companyIds, failureCallback, primaryCompanyId, removedCompanyIds },
}: ReturnType<typeof addPersonCompanies.request>) {
  try {
    const companiesFilter: PlatformFilterModel = yield call(getPersonCompaniesFilter);
    const org: Organization = yield select(getOrganization);

    const personResponse: Person = yield callApi("fetchPerson", org.id, id);

    const updatedCompanyIds = Array.from(
      new Set([
        ...companyIds,
        ...personResponse.accounts
          .filter(({ id }) => !removedCompanyIds?.includes(id))
          .map(({ id }) => id),
      ])
    );

    const [person, companiesResponse]: [Person, ListResponse<Company>] = yield all([
      callApi("updatePerson", org.id, undefined, {
        id,
        accounts: updatedCompanyIds.map((id) => ({ id })),
        primaryAccount: primaryCompanyId ? { id: primaryCompanyId } : null,
      }),
      callApi("fetchCompanies", org.id, {
        $filters: {
          $and: [
            {
              id: { $in: updatedCompanyIds },
            },
          ],
          ...companiesFilter,
        },
        $limit: RELATIONSHIP_DATA_LOAD_LIMIT,
        $order: personCompaniesOrder,
      }),
    ]);

    if (companyIds.length) {
      const messageArgs: SourceObjectMessageArgs = yield call(
        selectSourceObjectMessageArgs,
        EntityType.PERSON,
        person.sourceObject
      );
      notification.success({
        message: i18nService.formatMessage(
          messages.addCompanySuccess,
          "Person successfully related to Companies",
          { ...messageArgs, count: companyIds.length }
        ),
      });
    }
    if (removedCompanyIds?.length) {
      notification.success({
        message: i18nService.formatMessage(
          messages.removeCompanySuccess,
          "Successfully removed Companies",
          { count: removedCompanyIds.length }
        ),
      });
    }

    yield put(
      addPersonCompanies.success({
        companies: companiesResponse.data,
        total: person.accounts.length,
      })
    );
    yield put(notifyAboutChanges({ entityType: EntityType.PERSON, updated: [person] }));
  } catch (error) {
    yield put(addPersonCompanies.failure());
    yield put(handleError({ error }));
    if (failureCallback) {
      yield call(failureCallback);
    }
  }
}

function* onRemovePersonCompany({
  payload: { id, companyId, layoutId },
}: ReturnType<typeof removePersonCompany.request>) {
  try {
    const org: Organization = yield select(getOrganization);
    const recordData: PersonRecordData = yield select(getRecordData);

    // Need to fetch person as all companies might not be loaded for record
    const response: Person = yield callApi("fetchPerson", org.id, id);
    const person: Person = yield callApi("updatePerson", org.id, layoutId, {
      id,
      accounts: response.accounts.filter(({ id }) => id !== companyId).map(({ id }) => ({ id })),
    });

    yield put(
      removePersonCompany.success(recordData.companies?.filter(({ id }) => id !== companyId) ?? [])
    );
    yield put(notifyAboutChanges({ entityType: EntityType.PERSON, updated: [person] }));
  } catch (error) {
    yield put(removePersonCompany.failure());
    yield put(handleError({ error }));
  }
}

function* onAddPersonDeals({
  payload: { id, dealIds, removedDealsIds },
}: ReturnType<typeof addPersonDeals.request>) {
  try {
    const org: Organization = yield select(getOrganization);

    // Remove Deals Association
    yield all(
      (removedDealsIds ?? []).map((dealId) =>
        callApi("updateDeal", org.id, undefined, {
          id: dealId,
          contact: null,
        })
      )
    );

    yield all(
      (dealIds ?? []).map((dId: Deal["id"]) =>
        callApi("updateDeal", org.id, undefined, {
          id: dId,
          contact: { id },
        })
      )
    );

    if (dealIds?.length) {
      const panePerson: Person | undefined = yield select(getPerson);
      const messageArgs: SourceObjectMessageArgs = yield call(
        selectPaneSourceObjectMessageArgs,
        EntityType.PERSON,
        panePerson,
        id
      );
      notification.success({
        message: i18nService.formatMessage(
          messages.addDealSuccess,
          "Person successfully related to Deal",
          { ...messageArgs, count: dealIds.length }
        ),
      });
    }

    if (removedDealsIds?.length) {
      notification.success({
        message: i18nService.formatMessage(
          messages.removeDealSuccess,
          "Successfully removed Deals",
          { count: removedDealsIds.length }
        ),
      });
    }

    const pinLegends: PinLegendsFilter = yield call(getPinLegendsFilter, [EntityType.DEAL]);
    const dealsResponse: ListResponse<Deal> = yield callApi("fetchDeals", org.id, {
      $filters: {
        $and: [{ contactId: id }],
        includeAccessStatus: true,
        includeCustomFields: true,
        includeGroups: true,
        includeTerritories: true,
        pinLegends,
      },
      $limit: RELATIONSHIP_DATA_LOAD_LIMIT,
      $order: "name",
    });

    yield put(addPersonDeals.success({ deals: dealsResponse.data, total: dealsResponse.total }));
    yield put(notifyAboutChanges({ entityType: EntityType.DEAL, updated: dealsResponse.data }));
  } catch (error) {
    yield put(addPersonDeals.failure());
    yield put(handleError({ error }));
  }
}

function* onRemovePersonDeal({ payload: dealId }: ReturnType<typeof removePersonDeal.request>) {
  try {
    const org: Organization = yield select(getOrganization);

    yield callApi("updateDeal", org.id, undefined, {
      id: dealId,
      contact: null,
    });
    yield put(removePersonDeal.success(dealId));
  } catch (error) {
    yield put(removePersonDeal.failure(dealId));
    yield put(handleError({ error }));
  }
}

export function* onRemovePersonFromRoute({
  payload: { id, routeId },
}: ReturnType<typeof removePersonFromRoute.request>) {
  try {
    const org: Organization = yield select(getOrganization);
    const recordData: PersonRecordData = yield select(getRecordData);
    const route = recordData.routes.find(({ id }) => id === routeId);
    const stopId = route?.stopTime.find(
      (stop): stop is RoutePersonStop => stop.entity === EntityType.PERSON && stop.contactId === id
    )?.id;
    if (route === undefined || stopId === undefined) {
      yield put(removePersonFromRoute.failure(routeId));
      yield put(
        handleError({ error: new Error(`Pivot id not found for person ${id} on route ${routeId}`) })
      );
      return;
    }

    const survivors = route.stopTime.filter((stop) => stop.id !== stopId);
    const survivorsComplete = isRouteComplete(survivors);
    let metrics: RoutePreviewMetrics | undefined;
    // A delete is never geometry-neutral, so with no envelope the BE nulls every metric. When the
    // survivors leave the route complete, price the whole drive as a Re-build does.
    if (survivors.length > 0) {
      try {
        const preview: RoutePreview = yield callApi(
          "previewUnifiedRoute",
          org.id,
          buildPreviewPayloadFromRoute(
            { routeDetail: route.routeDetail, stopTime: survivors },
            { allStops: survivorsComplete }
          )
        );
        metrics = getRoutePreviewMetrics(preview);
      } catch (error) {
        // Best-effort: preview failure must not block the removal — log, no toast.
        yield put(handleError({ displayNotification: false, error }));
      }
    }

    yield callApi(
      "bulkUpdateUnifiedRouteStops",
      org.id,
      routeId,
      [{ id: stopId, _action_: "d" }],
      metrics
    );
    yield put(removePersonFromRoute.success(routeId));
  } catch (error) {
    yield put(removePersonFromRoute.failure(routeId));
    yield put(handleError({ error }));
  }
}

export function* onUpdatePerson({
  payload: {
    callback,
    customFields,
    fieldsValueValidateCallback,
    groupIdsToAdd,
    groupIdsToRemove,
    layoutId,
    person,
  },
}: ReturnType<typeof updatePerson.request>) {
  const intl = i18nService.getIntl();
  try {
    const companiesFilter: PlatformFilterModel = yield call(getPersonCompaniesFilter);
    const orgId: Organization["id"] = yield select(getOrganizationId);
    const recordData: PersonRecordData = yield select(getRecordData);

    const readOnlyFields = new Set(
      personFieldModel.fields
        .filter((field) => !field.isEditable || field.hasFeature(FieldFeature.CALCULATED_FIELD))
        .map((field) => field.name)
    );

    const changedCustomFields = Object.values(customFields ?? {}).filter((customField) => {
      if (!customField) {
        return false;
      }
      if (readOnlyFields.has(customField.esKey)) {
        return false;
      }

      const oldCustomField = recordData.person?.customFields?.find(
        ({ customField: { id } }) => id === customField.customField.id
      );
      return !oldCustomField || !isEqual(oldCustomField.value, customField.value);
    });

    if (fieldsValueValidateCallback) {
      invariant(!!layoutId, "Must use layoutId to validate fields");

      // Validate fields values
      let fieldErrors: FieldValidationErrors[] = [];

      const standardFieldsValidationErrors: FieldValidationErrors[] = yield callApi(
        "validateStandardFields",
        orgId,
        layoutId,
        EntityType.PERSON,
        person
      );
      if (standardFieldsValidationErrors.length) {
        fieldErrors = standardFieldsValidationErrors;
      }

      if (changedCustomFields.length) {
        const customFieldValidateResponse: FieldValidationErrors[] = yield callApi(
          "validateCustomFieldsValues",
          orgId,
          layoutId,
          EntityType.PERSON,
          undefined,
          changedCustomFields
        );

        if (customFieldValidateResponse.length) {
          fieldErrors = [...fieldErrors, ...customFieldValidateResponse];
        }
      }

      if (fieldErrors.length) {
        yield call(fieldsValueValidateCallback, fieldErrors);
        yield put(updatePerson.failure());
        return;
      }
    }

    const strippedPerson = readOnlyFields.size
      ? // TODO: implement proper fields deletion and type casting
        (omit(person, Array.from(readOnlyFields)) as Identified)
      : person;
    let updatedPerson: Person = yield callApi(
      "updatePerson",
      orgId,
      layoutId,
      strippedPerson,
      undefined,
      false
    );

    if (changedCustomFields.length) {
      invariant(!!layoutId, "Must use layoutId to update custom fields");
      yield callApi(
        "upsertCustomFieldsValues",
        orgId,
        layoutId,
        EntityType.PERSON,
        person.id,
        changedCustomFields
      );
    }

    const peopleIds: Person["id"][] = [person.id];
    yield all([
      ...(groupIdsToAdd ?? []).map((groupId) =>
        callApi("addToGroup", orgId, groupId, EntityType.PERSON, peopleIds)
      ),
      ...(groupIdsToRemove ?? []).map((groupId) =>
        callApi("deleteFromGroup", orgId, groupId, EntityType.PERSON, peopleIds)
      ),
    ]);

    const response: ListResponse<Person> = yield callApi(
      "fetchPins",
      orgId,
      yield call(getFetchPersonPayload, person.id)
    );
    // The PUT response comes from the database; the fetchPins one may lack sourceObject
    const savedSourceObject = updatedPerson.sourceObject;
    if (response.data.length) {
      updatedPerson = response.data[0];
    }

    if (intl) {
      const messageArgs: SourceObjectMessageArgs = yield call(
        selectSourceObjectMessageArgs,
        EntityType.PERSON,
        savedSourceObject
      );
      notification.success({
        message: getSuccessNotificationNode(
          intl,
          intl.formatMessage(messages.updateSuccess, messageArgs)
        ),
      });
    }

    let totalCompanies: number = 0;
    let companies: Company[] = [];
    if (updatedPerson.accounts.length) {
      const companiesResponse: ListResponse<Company> = yield callApi("fetchCompanies", orgId, {
        $filters: {
          $and: [
            {
              id: {
                $in: updatedPerson.accounts
                  ?.map((account) => account.id)
                  .slice(0, RELATIONSHIP_DATA_LOAD_LIMIT),
              },
            },
          ],
          ...companiesFilter,
        },
        $limit: RELATIONSHIP_DATA_LOAD_LIMIT,
        $order: personCompaniesOrder,
      });
      companies = companiesResponse.data;
      totalCompanies = companiesResponse.total;
    }

    yield put(updatePerson.success({ companies, person: updatedPerson, totalCompanies }));
    yield put(notifyAboutChanges({ entityType: EntityType.PERSON, updated: [updatedPerson] }));
    if (callback) {
      // we should only call callback after updatePerson.success
      yield call(callback, updatedPerson);
    }
  } catch (error) {
    yield put(updatePerson.failure());
    const panePerson: Person | undefined = yield select(getPerson);
    const messageArgs: SourceObjectMessageArgs = yield call(
      selectPaneSourceObjectMessageArgs,
      EntityType.PERSON,
      panePerson,
      person.id
    );
    yield put(
      handleError({
        defaultMessage: messages.updateError,
        defaultMessageValues: messageArgs,
        error,
      })
    );
  }
}

export function* onCreatePersonNote({ payload }: ReturnType<typeof createPersonNote.request>) {
  try {
    const org: Organization = yield select(getOrganization);
    const person: Person = yield select(getPerson);
    const note: Note = yield callApi("createNote", org.id, EntityType.PERSON, person.id, payload);
    yield put(createPersonNote.success());
    yield put(
      fetchNotes.request({
        filter: createEmptyFilterModel(),
        sort: [
          { field: noteFieldModel.getByName(NoteFieldName.UPDATED_AT)!, order: SortOrder.DESC },
        ],
      })
    );
    yield put(
      notifyAboutChanges({
        entityType: EntityType.PERSON,
        updated: [{ ...person, notes: [...(person.notes ?? []), note] }],
      })
    );
  } catch (error) {
    yield put(createPersonNote.failure());
    yield put(handleError({ error }));
  }
}

export function* onUpdatePersonNote({ payload }: ReturnType<typeof updatePersonNote.request>) {
  try {
    const person: Person = yield select(getPerson);
    const updatedNote: Note = yield callApi("updateNote", EntityType.PERSON, person.id, payload);
    yield put(updatePersonNote.success(updatedNote));
    yield put(
      notifyAboutChanges({
        entityType: EntityType.PERSON,
        updated: [
          {
            ...person,
            notes: (person.notes ?? []).map((note) =>
              note.id === updatedNote.id ? updatedNote : note
            ),
          },
        ],
      })
    );
  } catch (error) {
    yield put(updatePersonNote.failure(payload.id));
    yield put(handleError({ error }));
  }
}

export function* onUploadPersonFiles({ payload }: ReturnType<typeof uploadPersonFiles.request>) {
  try {
    const org: Organization = yield select(getOrganization);
    const person: Person = yield select(getPerson);
    const fileGroupId = payload.fileGroupId ?? uuid.v4();
    const responses: SettleResult<RawFile>[] = yield allSettled(
      payload.files.map((file) =>
        callApi("createFile", org.id, file, false, EntityType.PERSON, person.id, {
          headers: {
            "x-mmc-file-group-id": fileGroupId,
          },
        })
      )
    );
    const fileList: FileListItem[] = responses.map((response, index) => ({
      file: payload.files[index],
      uploading: false,
      ...(response.error
        ? { errored: true, errorMessage: String(response.result) }
        : { errored: false, uploadedFile: response.result }),
    }));
    payload.callback?.(fileList);
    yield put(uploadPersonFiles.success(fileList));
  } catch (error) {
    payload.callback?.(payload.files.map((file) => ({ errored: true, file, uploading: false })));
    yield put(uploadPersonFiles.failure());
    yield put(handleError({ error }));
  }
}

export function* onDeletePersonFile({ payload }: ReturnType<typeof deletePersonFile.request>) {
  try {
    const org: Organization = yield select(getOrganization);
    const person: Person = yield select(getPerson);

    const notificationKey = `file_removal_${payload.id}`;
    const cancelled = new Promise<true>((resolve) => {
      notification.info({
        duration: NOTIFICATION_DURATION_WITH_ACTION,
        key: notificationKey,
        message: getFileRemovedNotificationNode(i18nService.getIntl(), payload, () => {
          resolve(true);
          notification.close(notificationKey);
        }),
      });
    });

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    let markWindowClosed = (flag: true) => {};
    const windowClosed = new Promise<boolean>((resolve) => {
      markWindowClosed = resolve; // I need to "extract" it from here, so that I can call removeEventListener later
    });
    const documentListener = () => {
      if (document.visibilityState === "hidden") {
        markWindowClosed(true);
        notification.close(notificationKey);
      }
    };
    document.addEventListener("visibilitychange", documentListener);
    const windowListener = () => markWindowClosed(true);
    window.addEventListener("beforeunload", windowListener);

    const result: { cancelled?: Promise<true>; delete?: true; windowClosed?: Promise<true> } =
      yield race({
        cancelled,
        delete: delay(NOTIFICATION_DURATION_WITH_ACTION * 1000 /* since it is in seconds */),
        windowClosed,
      });

    window.removeEventListener("beforeunload", windowListener);
    document.removeEventListener("visibilitychange", documentListener);

    if (result.delete || result.windowClosed) {
      yield callApi("deleteEntityFile", org.id, EntityType.PERSON, person.id, payload.id);
      yield put(deletePersonFile.success({ file: payload, removed: true }));
    } else {
      yield put(deletePersonFile.success({ file: payload, removed: false }));
    }
  } catch (error) {
    yield put(deletePersonFile.failure(payload));
    yield put(handleError({ error }));
  }
}

export function* onDownloadPersonFile({ payload: file }: ReturnType<typeof downloadPersonFile>) {
  try {
    const org: Organization = yield select(getOrganization);
    const person: Person = yield select(getPerson);

    const key = `download_file_${file.id}`;
    const timeout = setTimeout(() => {
      notification.info({
        duration: 0, // don't close automatically, we'll close it ourselves
        key,
        message: getYourDownloadWillStartShortlyNode(i18nService.getIntl(), file.name),
      });
    }, 2000);

    const fileBlob: Blob = yield callApi(
      "fetchFile",
      org.id,
      file.id,
      true,
      false,
      EntityType.PERSON,
      person.id,
      { responseType: "blob", timeout: 0 }
    );
    clearTimeout(timeout);
    notification.close(key);

    yield call(downloadFileByUrl, window.URL.createObjectURL(fileBlob), file.name);
  } catch (error) {
    yield put(handleError({ error }));
  }
}

export function* onDeletePersonNote({ payload }: ReturnType<typeof deletePersonNote.request>) {
  try {
    const person: Person = yield select(getPerson);
    yield callApi("deleteNote", EntityType.PERSON, person.id, payload);
    yield put(deletePersonNote.success(payload.id));
    yield put(fetchNotes.request());
    yield put(
      notifyAboutChanges({
        entityType: EntityType.PERSON,
        updated: [
          {
            ...person,
            notes: (person.notes ?? []).filter((note) => note.id !== payload.id),
          },
        ],
      })
    );
  } catch (error) {
    yield put(deletePersonNote.failure(payload.id));
    yield put(handleError({ error }));
  }
}

export function* onPostponeActivity({ payload }: ReturnType<typeof postponeActivity.request>) {
  try {
    const organization: Organization = yield select(getOrganization);
    const layoutId = activityLayoutModel.getLayoutFor(payload).id;
    const { files, ...activity } = payload; // excluding files from payload

    const updatedActivity: Activity = yield callApi(
      "updateActivity",
      organization.id,
      layoutId,
      activity
    );
    yield put(postponeActivity.success(updatedActivity));

    notification.success({
      message: getSuccessNotificationNode(
        i18nService.getIntl(),
        i18nService.formatMessage(messages.postponeSuccess, "Activity postponed successfully")
      ),
    });
  } catch (error) {
    yield put(postponeActivity.failure(payload.id));
    yield put(handleError({ error }));
  }
}

export function* onToggleCompleteActivity({
  payload,
}: ReturnType<typeof toggleCompleteActivity.request>) {
  try {
    const organization: Organization = yield select(getOrganization);
    const layoutId = activityLayoutModel.getLayoutFor(payload).id;
    const { files, ...activity } = payload; // excluding files from payload

    const response: Activity = yield callApi("updateActivity", organization.id, layoutId, {
      ...activity,
      completed: !activity.completed,
    });
    yield put(toggleCompleteActivity.success(response));

    notification.success({
      message: getSuccessNotificationNode(
        i18nService.getIntl(),
        i18nService.formatMessage(
          messages.toggleCompleteSuccess,
          `Activity marked as "${response.completed ? "Done" : "Not yet done"}"`,
          { completed: response.completed }
        )
      ),
    });
  } catch (error) {
    yield put(toggleCompleteActivity.failure(payload.id));
    yield put(handleError({ error }));
  }
}

export function* onUpdateActivityNote({ payload }: ReturnType<typeof updateActivityNote.request>) {
  try {
    const organization: Organization = yield select(getOrganization);
    const layoutId = activityLayoutModel.getLayoutFor(payload.activity).id;
    const { files, ...activity } = payload.activity; // excluding files from payload

    const response: Activity = yield callApi("updateActivity", organization.id, layoutId, activity);
    yield put(
      updateActivityNote.success({
        id: response.id,
        note: response.note,
      })
    );
    yield call(payload.onSuccess);
  } catch (error) {
    yield put(updateActivityNote.failure(payload.activity.id));
    yield put(handleError({ error }));
  }
}

export function* onChangeActivitiesRecapRange({
  payload: recapRange,
}: ReturnType<typeof changeActivitiesRecapRange>) {
  yield call(localSettings.setRecapChartRange, recapRange);
}

export function* onChangeActivitiesSelectedActivityTypes({
  payload: activityTypes,
}: ReturnType<typeof changeActivitiesSelectedActivityTypes>) {
  const activeActivityTypes: ActivityType[] = yield select(getActiveActivityTypes);
  yield put(
    updateMetadata.request({
      recapChartTypesIds: getSavedActivityTypeIds(activityTypes, activeActivityTypes),
    })
  );
}

export function* onDeletePerson() {
  try {
    const org: Organization = yield select(getOrganization);
    const person: Person = yield select(getPerson);
    yield callApi("deletePerson", org.id, person.id);
    yield put(deletePerson.success());
    const messageArgs: SourceObjectMessageArgs = yield call(
      selectSourceObjectMessageArgs,
      EntityType.PERSON,
      person.sourceObject
    );
    notification.success({
      description: i18nService.formatMessage(
        messages.deleteSuccessDescription,
        "To restore this record, go to Data in Settings."
      ),
      message: i18nService.formatMessage(
        messages.deleteSuccess,
        "Person successfully deleted",
        messageArgs
      ),
    });
    yield put(notifyAboutChanges({ deletedIds: [person.id], entityType: EntityType.PERSON }));
  } catch (error) {
    yield put(deletePerson.failure());
    yield put(handleError({ error }));
  }
}

export function* onUpdatePersonFrequency({
  payload: { personId },
}: ReturnType<typeof updatePersonFrequency.request>) {
  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);
    const response: ListResponse<Person> = yield callApi(
      "fetchPins",
      orgId,
      yield call(getFetchPersonPayload, personId)
    );
    const person: Person = response.data[0];
    yield put(updatePersonFrequency.success(person));
  } catch (error) {
    yield put(updatePersonFrequency.failure());
    yield put(handleError({ error }));
  }
}

export function* onFetchDeals({ payload }: ReturnType<typeof fetchDeals.request>) {
  try {
    const org: Organization = yield select(getOrganization);
    const person: Person = yield select(getPerson);
    const recordData: PersonRecordData = yield select(getRecordData);

    const $offset = payload.loadMore ? recordData.deals.length : 0;
    const pinLegends: PinLegendsFilter = yield call(getPinLegendsFilter, [EntityType.DEAL]);

    const response: ListResponse<Deal> = yield callApi("fetchDeals", org.id, {
      $filters: {
        $and: [
          {
            contactId: person.id,
          },
        ],
        includeAccessStatus: true,
        includeCustomFields: true,
        includeGroups: true,
        includeTerritories: true,
        pinLegends,
      },
      $limit: RELATIONSHIP_DATA_LOAD_LIMIT,
      $offset,
      $order: "name",
    });
    yield put(
      fetchDeals.success({
        deals: payload.loadMore ? [...recordData.deals, ...response.data] : response.data,
        total: response.total,
      })
    );
  } catch (error) {
    yield put(fetchDeals.failure());
    yield put(handleError({ error }));
  }
}

export function* onFetchCompanies() {
  try {
    const companiesFilter: PlatformFilterModel = yield call(getPersonCompaniesFilter);
    const org: Organization = yield select(getOrganization);
    const person: Person = yield select(getPerson);
    const loadedPersonCompanies = person.accounts.slice(0, RELATIONSHIP_DATA_LOAD_LIMIT);

    const response: ListResponse<Company> = yield callApi("fetchCompanies", org.id, {
      $filters: {
        $and: [{ id: { $in: loadedPersonCompanies.map(({ id }) => id) } }],
        ...companiesFilter,
      },
      $limit: RELATIONSHIP_DATA_LOAD_LIMIT,
      $order: personCompaniesOrder,
    });

    const moreCompanies = processPersonCompanies({
      accessibleCompanies: response.data,
      allCompanies: loadedPersonCompanies,
    }).sort(nameComparator);
    yield put(fetchCompanies.success({ companies: moreCompanies }));
  } catch (error) {
    yield put(fetchCompanies.failure());
    yield put(handleError({ error }));
  }
}

export function* onFetchMoreCompanies() {
  try {
    const companiesFilter: PlatformFilterModel = yield call(getPersonCompaniesFilter);
    const org: Organization = yield select(getOrganization);
    const person: Person = yield select(getPerson);
    const recordData: PersonRecordData = yield select(getRecordData);
    const offset = recordData.companies.length;
    const loadedPersonCompanies = person.accounts.slice(
      offset,
      offset + RELATIONSHIP_DATA_LOAD_LIMIT
    );

    const response: ListResponse<Company> = yield callApi("fetchCompanies", org.id, {
      $filters: {
        $and: [{ id: { $in: loadedPersonCompanies.map(({ id }) => id) } }],
        ...companiesFilter,
      },
      $limit: RELATIONSHIP_DATA_LOAD_LIMIT,
      $order: personCompaniesOrder,
    });

    const moreCompanies = processPersonCompanies({
      accessibleCompanies: response.data,
      allCompanies: loadedPersonCompanies,
    }).sort(nameComparator);
    yield put(fetchMoreCompanies.success(moreCompanies));
  } catch (error) {
    yield put(fetchMoreCompanies.failure());
    yield put(handleError({ error }));
  }
}

export function* onFetchPins({ payload }: ReturnType<typeof fetchPins.request>) {
  try {
    const isColorChanged = !!payload.request?.colorKey?.[EntityType.PERSON];
    const isShapeChanged = !!payload.request?.shapeKey?.[EntityType.PERSON];

    if (!isColorChanged && !isShapeChanged) {
      return;
    }

    const person: Person | undefined = yield select(getPerson);
    if (!person) {
      return;
    }

    const organization: Organization = yield select(getOrganization);

    yield take(applyMapViewSettings);

    const response: ListResponse<Person> = yield callApi(
      "fetchPins",
      organization.id,
      yield call(getFetchPersonPayload, person.id)
    );

    const fetchedPerson: Person = response.data[0];

    if (isColorChanged) {
      yield put(setPersonColor(fetchedPerson.color));
    }

    if (isShapeChanged) {
      yield put(setPersonShape(fetchedPerson.shape));
    }
  } catch (error) {
    handleError({ error });
  }
}

export function* onUpdatePersonPrimaryCompany({
  payload,
}: ReturnType<typeof updatePersonPrimaryCompany.request>) {
  try {
    const companiesFilter: PlatformFilterModel = yield call(getPersonCompaniesFilter);
    const { id, accounts, primaryCompanyId } = payload;
    const orgId: Organization["id"] = yield select(getOrganizationId);

    const [person, companiesResponse]: [Person, ListResponse<Company>] = yield all([
      callApi("updatePerson", orgId, undefined, {
        id,
        accounts,
        primaryAccount: primaryCompanyId ? { id: primaryCompanyId } : null,
      }),
      callApi("fetchCompanies", orgId, {
        $filters: {
          $and: [
            {
              id: { $in: accounts.map(({ id }) => id) },
            },
          ],
          ...companiesFilter,
        },
        $limit: RELATIONSHIP_DATA_LOAD_LIMIT,
        $order: personCompaniesOrder,
      }),
    ]);
    yield put(notifyAboutChanges({ entityType: EntityType.PERSON, updated: [person] }));

    yield put(
      updatePersonPrimaryCompany.success({
        companies: companiesResponse.data,
        total: person.accounts.length,
      })
    );
  } catch (error) {
    yield put(updatePersonPrimaryCompany.failure());
    yield put(handleError({ error }));
  }
}

export function* onSubscribeEmail() {
  try {
    const organization: Organization = yield select(getOrganization);
    const recordData: PersonRecordData = yield select(getRecordData);

    if (!recordData.person) {
      return;
    }

    yield callApi("subscribeEntityEmail", organization.id, EntityType.PERSON, recordData.person.id);

    yield put(
      notifyAboutChanges({
        entityType: EntityType.PERSON,
        updated: [{ ...recordData.person, subscribed: true }],
      })
    );

    yield put(subscribePersonEmail.success());
  } catch (error) {
    yield put(subscribePersonEmail.failure());
    handleError({ error });
  }
}

export function* onUnsubscribeEmail() {
  try {
    const organization: Organization = yield select(getOrganization);
    const recordData: PersonRecordData = yield select(getRecordData);

    if (!recordData.person) {
      return;
    }

    yield callApi(
      "unsubscribeEntityEmail",
      organization.id,
      EntityType.PERSON,
      recordData.person.id
    );

    yield put(
      notifyAboutChanges({
        entityType: EntityType.PERSON,
        updated: [{ ...recordData.person, subscribed: false }],
      })
    );

    yield put(unsubscribePersonEmail.success());
  } catch (error) {
    yield put(unsubscribePersonEmail.failure());
    handleError({ error });
  }
}

function* onShowEntityView({ payload }: ReturnType<typeof showEntityView>) {
  const entityTypesWithExternalPreview: EntityType[] = yield select(
    getEntityTypesWithExternalPreview
  );

  if (payload.entityType && !entityTypesWithExternalPreview.includes(payload.entityType)) {
    return;
  }

  const { pathname }: RouterLocation<any> = yield select(getLocation);
  const isOnListPage = pathname.startsWith(Path.PEOPLE);

  if (isOnListPage) {
    const source: EntityViewSource = yield select(getEntityViewSource);

    if (source === EntityViewSource.GLOBAL_SEARCH) {
      yield put(push(`${Path.PEOPLE}/${payload.entityId}`));
    }
  }
}

export function* onBulkUpdateFiles({
  payload: { callback, files },
}: ReturnType<typeof bulkUpdateFiles.request>) {
  try {
    const organization: Organization = yield select(getOrganization);
    const recordData: PersonRecordData = yield select(getRecordData);

    if (!recordData.person) {
      return;
    }

    yield callApi("bulkUpdateFile", organization.id, files);

    const updatedFilesResponse: ListResponse<RawFile> = yield callApi(
      "fetchEntityFiles",
      organization.id,
      EntityType.PERSON,
      recordData.person.id,
      {
        $limit: 1000,
        $order: "-createdAt",
      }
    );

    if (callback) {
      yield call(callback);
    }

    yield put(bulkUpdateFiles.success(updatedFilesResponse.data));
  } catch (error) {
    yield put(bulkUpdateFiles.failure(error));
    yield put(handleError({ error }));
  }
}

export function* onFetchNotes() {
  try {
    const filter: FilterModel | undefined = yield select(getNotesFilter);
    const sort: SortModel = yield select(getNotesSort);
    const recordData: PersonRecordData = yield select(getRecordData);

    if (!recordData.person) {
      return;
    }

    const response: ListResponse<Note> = yield callApi(
      "fetchNotes",
      EntityType.PERSON,
      recordData.person.id,
      {
        $filters: filter ? convertToPlatformFilterModel(filter, [], noteFieldModel) : undefined,
        $limit: 1000,
        $order: convertToPlatformSortModel(sort),
      }
    );
    yield put(fetchNotes.success({ data: response.data, total: response.accessible }));
  } catch (error) {
    yield put(fetchNotes.failure());
    yield put(handleError({ error }));
  }
}

export function* onSuppressPartialAddressWarning() {
  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);
    const person: Person = yield select(getPerson);
    yield callApi("updatePerson", orgId, undefined, {
      id: person.id,
      suppressPartialAddressWarning: true,
    });
    yield put(suppressPartialAddressWarning.success());
  } catch (error) {
    yield put(suppressPartialAddressWarning.failure());
    yield put(handleError({ error }));
  }
}

export function* personRecordSaga() {
  yield takeEvery(addPersonDeals.request, onAddPersonDeals);
  yield takeEvery(addPersonCompanies.request, onAddPersonCompany);
  yield takeEvery(createPersonNote.request, onCreatePersonNote);
  yield takeEvery(deletePersonFile.request, onDeletePersonFile);
  yield takeEvery(deletePersonNote.request, onDeletePersonNote);
  yield takeEvery(downloadPersonFile, onDownloadPersonFile);
  yield takeLatest(fetchPersonRoutes.request, onFetchPersonRoutes);
  yield takeLatest(fetchPersonActivities.request, onFetchPersonActivities);
  yield takeLatest(
    fetchPersonActivitiesCompletedByType.request,
    onFetchPersonActivitiesCompletedByType
  );
  yield takeLatest(fetchPreviewData.request, onFetchPreviewData);
  yield takeLatest(fetchPersonActivitiesTotal.request, onFetchPersonActivitiesTotal);
  yield takeLatest(fetchPerson.request, onFetchPerson);
  yield takeLatest(fetchDeals.request, onFetchDeals);
  yield takeEvery(postponeActivity.request, onPostponeActivity);
  yield takeEvery(removePersonDeal.request, onRemovePersonDeal);
  yield takeEvery(removePersonCompany.request, onRemovePersonCompany);
  yield takeEvery(removePersonFromRoute.request, onRemovePersonFromRoute);
  yield takeEvery(toggleCompleteActivity.request, onToggleCompleteActivity);
  yield takeEvery(updateActivityNote.request, onUpdateActivityNote);
  yield takeEvery(updatePerson.request, onUpdatePerson);
  yield takeEvery(uploadPersonFiles.request, onUploadPersonFiles);
  yield takeEvery(updatePersonNote.request, onUpdatePersonNote);
  yield takeEvery(changeActivitiesRecapRange, onChangeActivitiesRecapRange);
  yield takeEvery(changeActivitiesSelectedActivityTypes, onChangeActivitiesSelectedActivityTypes);
  yield takeEvery(deletePerson.request, onDeletePerson);
  yield takeEvery(fetchThumbnail, onFetchThumbnail);
  yield takeEvery(updatePersonFrequency.request, onUpdatePersonFrequency);
  yield takeLeading(fetchMoreCompanies.request, onFetchMoreCompanies);
  yield takeLatest(fetchCompanies.request, onFetchCompanies);
  yield takeEvery(fetchPins.request, onFetchPins);
  yield takeLatest(updatePersonPrimaryCompany.request, onUpdatePersonPrimaryCompany);
  yield takeLatest(subscribePersonEmail.request, onSubscribeEmail);
  yield takeLatest(unsubscribePersonEmail.request, onUnsubscribeEmail);
  yield takeLatest(showEntityView, onShowEntityView);
  yield takeLatest(bulkUpdateFiles.request, onBulkUpdateFiles);
  yield takeLatest(fetchNotes.request, onFetchNotes);
  yield takeLeading(suppressPartialAddressWarning.request, onSuppressPartialAddressWarning);
}
